import { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const i18n = defineMessages({
	crossflowLoomPromptText: {
		id: 'home.loom.crossflow-prompt-text.non-final',
		defaultMessage:
			'Create a page summarizing key moments, actions, and open questions from this Loom video: {loomUrl}',
		description:
			'Auto-sent Rovo prompt when a user arrives on /wiki/home from Loom. {loomUrl} is the Loom share URL.',
	},
});

const useVideoId = createSingleQueryParamHook('loomCrossflowVideoId');
// Exclude path separators, queries and fragments so the ID cannot escape the share URL path.
const LOOM_VIDEO_ID_PATTERN = /^[a-zA-Z0-9:-]+$/;

export const useLoomCrossflowPrompt = (enabled = true): string | undefined => {
	const intl = useIntl();
	const { environment } = useSessionData();
	const videoIdParam = useVideoId();
	const videoId = enabled ? videoIdParam : undefined;
	const { createEvent } = useConfluenceAnalyticsEvents();
	const lastObservedVideoId = useRef<string | undefined>(undefined);
	const isValidVideoId = Boolean(videoId && LOOM_VIDEO_ID_PATTERN.test(videoId));

	useEffect(() => {
		if (videoId === lastObservedVideoId.current) return;
		lastObservedVideoId.current = videoId;
		if (videoId === undefined) return;
		// Count arrivals independently of eligibility; never send the ID or prompt.
		createEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'received',
				actionSubject: 'loomCrossflow',
				actionSubjectId: 'rovoChatPrompt',
				source: 'confluence-loom-crossflow',
				attributes: {
					hasLoomCrossflowVideoIdParam: true,
					loomVideoIdStatus: videoId === '' ? 'empty' : isValidVideoId ? 'valid' : 'invalid',
				},
			},
		}).fire();
	}, [videoId, isValidVideoId, createEvent]);

	if (!isValidVideoId) return undefined;
	const loomOrigin =
		environment === 'PRODUCTION' ? 'https://www.loom.com' : 'https://stage.loom.com';
	return intl.formatMessage(i18n.crossflowLoomPromptText, {
		loomUrl: `${loomOrigin}/share/${videoId}`,
	});
};
